// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useMemo, useRef, useState } from 'react';

import type { CaseData } from '../../../app/bygning/useCaseData';
import { reportedTypes } from '../../../bygning/checklist';
import {
  canSend,
  CSV_FILENAME,
  emptyFractions,
  footStatus,
  FRACTION_NOTE,
  fractionsCsvHref,
  GATE_HINT_ID,
  SEND_NOTICE,
} from '../../../indberetning/model';
import { Button, ButtonLink } from '../../Button';
import { EmptyState } from '../../EmptyState';
import { Spinner } from '../../Spinner';
import { TextLink } from '../../TextLink';
import { FractionTable } from '../../indberetning/FractionTable';
import { MaterialFlow } from './MaterialFlow';
import styles from './PagePreview.module.css';

export interface IndberetningSheetProps {
  data: CaseData;
  /** Re-read the case ("Prøv igen" when the fractions failed). */
  onRetry: () => void;
  /** A type in the Materialeflow: open it in Ressourcer. */
  onOpenType: (typeId: number) => void;
  /** The Materialeflow's Blokeret: "Det, der blokerer" (Gennemgang). */
  onShowBlocking: () => void;
}

/**
 * Rapport's Indberetning page — the old Indberetning screen, unchanged in
 * behaviour (CSV always, Send gated on `canSend`) — with the Materialeflow
 * over the pkt. 10 fractions it reports.
 */
export function IndberetningSheet({ data, onRetry, onOpenType, onShowBlocking }: IndberetningSheetProps) {
  const fractions = data.fractions;
  const [sendNotice, setSendNotice] = useState(false);
  const csvHref = useMemo(() => (fractions ? fractionsCsvHref(fractions) : null), [fractions]);
  const tableRef = useRef<HTMLHeadingElement>(null);

  if (!fractions || !csvHref) {
    return (
      <section className={styles.sheet} aria-label="Indberetning">
        <h2 className={styles.sheetTitle}>Indberetning</h2>
        {data.loading || !data.loaded ? (
          <Spinner label="Indlæser fraktioner…" />
        ) : (
          <p className={styles.p} role="alert">
            Affaldsfraktionerne kunne ikke hentes. <TextLink onClick={onRetry}>Prøv igen</TextLink>
          </p>
        )}
      </section>
    );
  }

  const sendable = canSend(fractions);
  const empty = emptyFractions(fractions, reportedTypes(data.types).length);
  const foot = footStatus(fractions);
  const showFractions = () => {
    const el = tableRef.current;
    if (!el) return;
    el.scrollIntoView({ block: 'start' });
    el.focus({ preventScroll: true });
  };
  return (
    <section className={styles.sheet} aria-labelledby="rapport-indberetning">
      <header className={styles.sheetHead}>
        <h2 id="rapport-indberetning" className={styles.sheetTitle}>
          Indberetning
        </h2>
        <span className={styles.sheetSub}>Affaldsfraktioner til bygningsaffald.dk</span>
        <div className={styles.sheetActions}>
          <ButtonLink size="sm" href={csvHref} download={CSV_FILENAME}>
            Hent fraktioner (CSV)
          </ButtonLink>
          {!sendable && (
            <span id={GATE_HINT_ID} className={styles.gateHint}>
              {foot.text}
            </span>
          )}
          <Button
            variant="primary"
            size="sm"
            disabled={!sendable}
            title={sendable ? undefined : foot.text}
            aria-describedby={sendable ? undefined : GATE_HINT_ID}
            onClick={() => setSendNotice(true)}
          >
            Send til bygningsaffald.dk
          </Button>
        </div>
      </header>
      {sendNotice && (
        <p className={styles.notice} role="status">
          {SEND_NOTICE}
        </p>
      )}
      <MaterialFlow
        types={data.types}
        samples={data.samples}
        fractions={fractions}
        onOpenType={onOpenType}
        onShowFractions={showFractions}
        onShowBlocking={onShowBlocking}
      />
      <section className={styles.part} aria-labelledby="rapport-pkt10">
        <div className={styles.partHead}>
          <h3 id="rapport-pkt10" ref={tableRef} tabIndex={-1} className={styles.partTitle}>
            Pkt. 10 · affaldstyper og fraktioner
          </h3>
          <span className={styles.sheetSub}>Del 2</span>
        </div>
        <p className={styles.note}>{FRACTION_NOTE}</p>
        {empty ? <EmptyState title={empty.title} detail={empty.detail} /> : <FractionTable fractions={fractions} />}
      </section>
    </section>
  );
}
